// noinspection JSUnusedGlobalSymbols -- Imported via d.ts file
import {ModalV3} from "../../../helpers/modals/ClassBased/ModalV3";
import createElement from "../../../helpers/createElement";
import {waitUntil} from "../../../helpers/waitUntil";

class MultimapTutorialModal extends ModalV3 {
    /** @var {MultimapTutorialModalDefs.Config} */
    config;

    /**
     * @param {MultimapTutorialModalDefs.Config} config
     */
    constructor(config) {
        const finalConfig = {
            metadata: {},
            mapStates: [],
            ...config,
        };

        // noinspection JSCheckFunctionSignatures -- Tutorial config extends the base modal config.
        super(finalConfig);

        this.config = finalConfig;

        this.render();
        setTimeout(() => this.display(true), 0);
    }

    init() {
        super.init();

        if (this.modalContainer.classList.contains('initialized')) {
            return;
        }

        this.startSelectingButton = this.modalContainer.querySelector('.multimap-tutorial-start-button');
        this.tabRows = this.modalContainer.querySelectorAll('.multimap-tutorial-tab-row');

        if (this.startSelectingButton instanceof HTMLButtonElement) {
            this.startSelectingButton.addEventListener('click', () => {
                this.hide();
            });
        }

        this.tabRows.forEach(tabRow => {
            tabRow.addEventListener('click', () => {
                this.hide();

                if (this.config.onTabRowClick) {
                    this.config.onTabRowClick(tabRow.dataset.mapId || '');
                }
            });
        });

        this.modalContainer.classList.add('initialized');
    }

    setupEvents() {
        super.setupEvents();

        if (!this.handleViewportResize) {
            this.handleViewportResize = () => this.updateArrowOverlay();
        }

        if (!this.resizeListenerActive) {
            window.addEventListener('resize', this.handleViewportResize);
            this.resizeListenerActive = true;
        }

        $(this.modalContainer).on('shown.bs.modal', () => {
            this.scheduleArrowOverlayUpdate();
        });

        $(this.modalContainer).on('hidden.bs.modal', () => {
            this.cleanupArrowOverlay();

            if (this.resizeListenerActive) {
                window.removeEventListener('resize', this.handleViewportResize);
                this.resizeListenerActive = false;
            }

            this.modalContainer.remove();
        });
    }

    display(firstTime = true) {
        super.display(firstTime);

        waitUntil(() => this.modalContainer.classList.contains('show'), {
            timeoutAfter: 2000,
            msBetweenAttempts: 50,
        }).then(() => {
            this.scheduleArrowOverlayUpdate();
            setTimeout(() => this.scheduleArrowOverlayUpdate(), 400);
        }).catch(() => {
        });
    }

    scheduleArrowOverlayUpdate() {
        const scheduleFrame = window.requestAnimationFrame || (callback => setTimeout(callback, 16));

        scheduleFrame(() => this.updateArrowOverlay());
    }

    hide() {
        super.hide();

        setTimeout(() => {
            if (this.modalContainer.classList.contains('show')) {
                this.forceHide();
            }
        }, 350);
    }

    forceHide() {
        this.modalContainer.classList.remove('show');
        this.modalContainer.style.display = 'none';
        document.body.classList.remove('modal-open');

        document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
        $(this.modalContainer).trigger('hidden.bs.modal');
    }

    updateArrowOverlay() {
        if (window.innerWidth < 992) {
            this.cleanupArrowOverlay();
            return;
        }

        const modalContent = this.modalContainer.querySelector('.modal-content');
        const tabButtons = Array.from(document.querySelectorAll('.multi-seat-map-selector .btn'))
            .filter(button => {
                const rect = button.getBoundingClientRect();

                return rect.width > 0 && rect.height > 0;
            });

        if (!modalContent || !this.modalContainer.classList.contains('show') || tabButtons.length < 2 || tabButtons.length > 4) {
            this.cleanupArrowOverlay();
            return;
        }

        const cardRect = modalContent.getBoundingClientRect();
        if (cardRect.width <= 0 || cardRect.height <= 0) {
            this.cleanupArrowOverlay();
            return;
        }

        const overlay = this.getOrCreateArrowOverlay();
        const width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
        const height = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

        overlay.setAttribute('viewBox', `0 0 ${width} ${height}`);
        overlay.setAttribute('width', `${width}`);
        overlay.setAttribute('height', `${height}`);
        overlay.innerHTML = this.getArrowMarkerSvg();

        tabButtons.forEach((button, index) => {
            const buttonRect = button.getBoundingClientRect();
            const startX = cardRect.left + (cardRect.width * this.getArrowStartRatio(tabButtons.length, index));
            const startY = cardRect.top + this.getArrowStartOffsetY(cardRect, tabButtons.length, index);
            const endX = buttonRect.left + (buttonRect.width / 2);
            const arrowTipY = buttonRect.bottom + 8;
            const arrowBaseY = arrowTipY + this.getArrowHeadBaseOffset();
            const verticalSegmentStartY = arrowBaseY + 10;
            const verticalDistance = Math.max(40, Math.abs(startY - arrowBaseY));
            const curveLift = Math.max(40, Math.min(80, verticalDistance * 0.45));
            const controlX = endX;
            const controlY = Math.max(startY, verticalSegmentStartY) + curveLift;
            const pathData = [
                `M ${startX} ${startY}`,
                `Q ${controlX} ${controlY} ${endX} ${verticalSegmentStartY}`,
                `L ${endX} ${arrowBaseY}`,
            ].join(' ');

            overlay.appendChild(this.createArrowPath(pathData, 'multimap-tutorial-arrow-body'));
            overlay.appendChild(this.createArrowPath(pathData, 'multimap-tutorial-arrow-accent'));
        });
    }

    getArrowStartRatio(tabCount, index) {
        const ratiosByTabCount = {
            2: [0.18, 0.82],
            3: [0.16, 0.5, 0.84],
            4: [0.12, 0.36, 0.64, 0.88],
        };

        return ratiosByTabCount[tabCount][index];
    }

    getArrowStartOffsetY(cardRect, tabCount, index) {
        const baseOffset = Math.min(90, Math.max(58, cardRect.height * 0.16));

        if (tabCount === 2 || (tabCount === 3 && index !== 1)) {
            return baseOffset + 24;
        }

        return baseOffset;
    }

    getArrowHeadBaseOffset() {
        return 23.4;
    }

    getOrCreateArrowOverlay() {
        if (this.arrowOverlay && document.body.contains(this.arrowOverlay)) {
            return this.arrowOverlay;
        }

        this.arrowOverlay = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        this.arrowOverlay.classList.add('multimap-tutorial-arrow-overlay');
        this.arrowOverlay.setAttribute('aria-hidden', 'true');
        this.arrowOverlay.setAttribute('focusable', 'false');
        this.arrowOverlay.style.position = 'fixed';
        this.arrowOverlay.style.inset = '0';
        this.arrowOverlay.style.zIndex = '1045';
        this.arrowOverlay.style.width = '100vw';
        this.arrowOverlay.style.height = '100vh';
        this.arrowOverlay.style.overflow = 'visible';
        this.arrowOverlay.style.pointerEvents = 'none';
        document.body.appendChild(this.arrowOverlay);

        return this.arrowOverlay;
    }

    getArrowMarkerSvg() {
        return `
            <defs>
                <marker id="multimap-tutorial-arrowhead" markerWidth="32.4" markerHeight="32.4" refX="16.2" refY="27" orient="0" markerUnits="userSpaceOnUse">
                    <path class="multimap-tutorial-arrowhead" d="M 3.6 27 L 16.2 3.6 L 28.8 27 Z"></path>
                </marker>
            </defs>
        `;
    }

    createArrowPath(pathData, className) {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        path.setAttribute('d', pathData);
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke-linecap', 'round');
        path.setAttribute('stroke-linejoin', 'round');
        path.classList.add(className);

        if (className === 'multimap-tutorial-arrow-accent') {
            path.setAttribute('marker-end', 'url(#multimap-tutorial-arrowhead)');
        }

        return path;
    }

    cleanupArrowOverlay() {
        if (this.arrowOverlay) {
            this.arrowOverlay.remove();
            this.arrowOverlay = null;
        }
    }

    render() {
        const metadata = this.config.metadata || {};
        const continueMetadata = metadata.continue || {};
        const title = metadata.title || 'Select Your Space';
        const intro = metadata.intro || 'Choose the type of space you would like to purchase by selecting one of the tabs:';
        const continueTitle = continueMetadata.title || 'Ready To Check Out?';
        const continueDescription = continueMetadata.description || 'Once you have made your selection, click the Continue button.';

        this.replace(createElement(`
            <div class="modal fade height-auto multimap-tutorial-modal" tabindex="-1" role="dialog">
                <div class="modal-dialog modal-dialog-centered" role="document" style="max-width: 540px; width: 95vw;">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">${this.escapeHtml(title)}</h5>
                            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div class="modal-body">
                            ${this.renderIcon(metadata.icon_html, 'multimap-tutorial-main-icon mb-3 mx-auto')}
                            <p class="multimap-tutorial-intro text-center mb-3">
                                ${this.escapeHtml(intro)}
                            </p>

                            <div class="multimap-tutorial-tab-rows">
                                ${this.renderTabRows()}
                            </div>

                            <div class="multimap-tutorial-continue-block text-center mt-4">
                                ${this.renderIcon(continueMetadata.icon_html, 'multimap-tutorial-continue-icon mb-2 mx-auto')}
                                <div class="font-weight-bold text-uppercase mb-1">
                                    ${this.escapeHtml(continueTitle)}
                                </div>
                                <div class="text-muted">
                                    ${this.escapeHtml(continueDescription)}
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer d-block">
                            <button type="button" class="btn btn-primary btn-block multimap-tutorial-start-button">
                                Start Selecting
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `), false);
    }

    /**
     * @return {string}
     */
    renderTabRows() {
        return this.getTabRows().map(row => {
            const color = this.normalizeColor(row.color);
            const colorStyle = color ? `border-left-color: ${this.escapeHtml(color)}; color: ${this.escapeHtml(color)};` : '';

            return `
                <button
                    type="button"
                    class="multimap-tutorial-tab-row d-flex align-items-center w-100 text-left mb-2 p-3 border rounded bg-white"
                    data-map-id="${this.escapeHtml(row.mapId)}"
                    style="border-left-width: 5px !important; ${colorStyle}"
                >
                    ${this.renderIcon(row.icon_html, 'multimap-tutorial-tab-icon mr-3 flex-shrink-0')}
                    <span class="d-block">
                        <span class="d-block font-weight-bold text-uppercase">
                            ${this.escapeHtml(row.title)}
                        </span>
                        <span class="d-block text-muted">
                            ${this.escapeHtml(row.description)}
                        </span>
                    </span>
                </button>
            `;
        }).join('');
    }

    /**
     * @return {Array}
     */
    getTabRows() {
        const metadataTabs = this.config.metadata && this.config.metadata.tabs ? this.config.metadata.tabs : {};

        return this.config.mapStates.map(mapState => {
            const configuredTab = metadataTabs[mapState.id] || null;

            if (configuredTab) {
                return {
                    mapId: mapState.id,
                    title: configuredTab.title || mapState.label || '',
                    description: configuredTab.description || this.getFallbackTabDescription(mapState),
                    color: configuredTab.color || '',
                    icon_html: configuredTab.icon_html || '',
                };
            }

            return {
                mapId: mapState.id,
                title: mapState.label || '',
                description: this.getFallbackTabDescription(mapState),
                color: '',
                icon_html: '',
            };
        });
    }

    /**
     * @param {MultimapTutorialModalDefs.SeatMapState} mapState
     * @return {string}
     */
    getFallbackTabDescription(mapState) {
        const singularLabel = mapState.entityLabels && mapState.entityLabels.singular
            ? mapState.entityLabels.singular
            : 'seat';

        return `Select an available ${singularLabel}.`;
    }

    /**
     * @param {string} iconHtml
     * @param {string} wrapperClass
     * @return {string}
     */
    renderIcon(iconHtml, wrapperClass) {
        if (!iconHtml) {
            return '';
        }

        return `
            <span
                class="multimap-tutorial-icon-wrapper ${this.escapeHtml(wrapperClass)}"
                aria-hidden="true"
                style="width: 2.5rem; height: 2.5rem; display: inline-flex; align-items: center; justify-content: center; font-size: 1.6rem;"
            >
                ${iconHtml}
            </span>
        `;
    }

    /**
     * @param {string} color
     * @return {string}
     */
    normalizeColor(color) {
        if (!color || typeof color !== 'string') {
            return '';
        }

        const trimmedColor = color.trim();

        if (/^#[0-9a-fA-F]{3,8}$/.test(trimmedColor)) {
            return trimmedColor;
        }

        return '';
    }

    /**
     * @param {string} value
     * @return {string}
     */
    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }
}

export {MultimapTutorialModal};
